import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { TemplateMenuObject } from '../interfaces';
import { delay } from '../../utils.service';

/** value in `px` */
const MENU_POPUP_MARGIN = 4;

@Component({
    selector: 'aza-rws-popup-menu',
    templateUrl: './popup-menu.component.html',
    standalone: false
})
export class PopupTemplatePopupMenuComponent<TemplateData extends {}>
    implements AfterViewInit
{
    @Input() templateRef: TemplateMenuObject<TemplateData>['templateRef'];
    @Input() templateData?: TemplateData;
    @Input() triggerHost: ElementRef<HTMLElement>;

    initialRootDomRect = rootDomRect();
    menuCSS?: Partial<CSSStyleDeclaration>;

    @ViewChild('htmlRef', { read: ElementRef })
    htmlRef?: ElementRef<HTMLDivElement>;

    constructor(private cdr: ChangeDetectorRef) {
        // prevent error expression changed after it has been checked
        cdr.detach;
    }

    async ngAfterViewInit(): Promise<void> {
        this.menuCSS = this.getMenuCss();
        this.cdr.reattach();
        this.cdr.detectChanges();
    }

    private getMenuCss(): Partial<CSSStyleDeclaration> {
        return {
            ...this.getMenuPosition(),
        };
    }

    private getMenuPosition(): Partial<CSSStyleDeclaration> {
        const hostRect = this.triggerHost.nativeElement.getBoundingClientRect();
        const getPositionParam: GetPositionParam = {
            hostRect,
            rootRect: this.initialRootDomRect,
            menuRect: this.htmlRef.nativeElement.getBoundingClientRect(),
        };

        return shouldBePositionedAbove(hostRect, window)
            ? getMenuAbovePosition(getPositionParam)
            : getMenuBelowPosition(getPositionParam);
    }
}

type GetPositionParam = {
    hostRect: DOMRect;
    rootRect: DOMRect;
    menuRect: DOMRect;
};

const getMenuAbovePosition = ({
    hostRect,
    rootRect,
    menuRect,
}: GetPositionParam) => {
    const top = getMenuAbovePositionTop({ hostRect, rootRect, menuRect });
    const isOverflowOnTop = top < 0;

    return {
        top: `${top - (isOverflowOnTop ? top : 0)}px`,
        left: `${hostRect.left}px`,
    } as Partial<CSSStyleDeclaration>;
};

const getMenuBelowPosition = ({
    hostRect,
    rootRect,
    menuRect,
}: GetPositionParam) => {
    const top = getMenuBelowPositionTop({ hostRect, rootRect, menuRect });
    const menuRectBottom = top + menuRect.height;
    const isOverflowOnBottom = menuRectBottom > rootRect.height;

    return {
        top: `${
            top - (isOverflowOnBottom ? menuRectBottom - rootRect.height : 0)
        }px`,
        left: `${hostRect.left}px`,
    } as Partial<CSSStyleDeclaration>;
};

const shouldBePositionedAbove = (hostRect: DOMRect, window: Window) => {
    const windowHalfHeight = window.innerHeight / 2;
    return hostRect.top > windowHalfHeight;
};

/** We use `aza-rws-root` because the main scrollbar is attached to `aza-rws-root` instead of the `body` */
const rootDomRect = () =>
    document.querySelector('aza-rws-root').getBoundingClientRect();

function getMenuBelowPositionTop({ hostRect, rootRect }: GetPositionParam) {
    return hostRect.bottom - rootRect.top + MENU_POPUP_MARGIN;
}

function getMenuAbovePositionTop({
    hostRect,
    rootRect,
    menuRect,
}: GetPositionParam): number {
    return hostRect.top - rootRect.top - MENU_POPUP_MARGIN - menuRect.height;
}
